import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output, ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {ConfirmationService, MenuItem, MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {CcBaseComponent} from "../../../../crystal-elements/cc-base.component";
import {PlResidualInfo} from "../../../../model/clearing/pl-residual-info";
import {TableColumnConfig} from "../../../../model/api/table-column-config";
import {TableButtonConfig} from "../../../../model/api/table-button-config";
import {ReportProfile} from "../../../../model/archive/report-profile";
import {FormFieldGroupConfig} from "../../../../model/api/form-field-group-config";
import {
  ClearingAllocationPlResidualService
} from "../../../../services/clearing/clearing-allocation-pl-residual.service";
import {PLRequest} from "../../../../model/clearing/pl-request";
import {TableRecordResult} from "../../../../model/api/table-record-result";
import {CcTableComponent} from "../../../../crystal-elements/table/cc-table.component";
import {AppConfigService} from "../../../../services/app-config.service";
import {EMPTY, exhaustMap, takeUntil, timer} from "rxjs";
import {catchError, filter, tap} from "rxjs/operators";


@Component({

  selector: 'app-pl-residual-table',
  templateUrl: './pl-residual-table.component.html',
  styleUrls: ['./pl-residual-table.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [

    MessageService,
    ConfirmationService,
    TranslateService,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PlResidualTableComponent extends CcBaseComponent {

  tableConfig: TableColumnConfig[] = [];

  headerButtonConfig: TableButtonConfig[] = [];

  contextMenuConfig: MenuItem[] = [];
  formRecord: PlResidualInfo;
  selectedRecords: PlResidualInfo;
  records: PlResidualInfo[] = [];
  recordsChange = new EventEmitter<PlResidualInfo[]>();

  totalRecords: number = 0;
  inProgressCount: number = 0;

  tableLoading: boolean = true;

  tableCurrentRequest: PLRequest;
  tableCurrentRequestChange = new EventEmitter<PLRequest>();

  @Input()
  transPartKey: string;
  @Input()
  visible: boolean = true;
  selectedProfile: ReportProfile;
  formConfig: FormFieldGroupConfig[];
  detailDialogVisible: boolean;

  @Input()
  globalResidual: number = 0;

  @Input()
  dialogFormVisible: boolean = false;
  @Output()
  dialogFormVisibleChange = new EventEmitter<boolean>();

  @Input()
  selectedOrderIds: string[];
  @Input()
  tradeModeEnabled: boolean;
  previousTradeModeEnabled: boolean | undefined = undefined;

  @ViewChild('ccTable')
  public ccTable: CcTableComponent;

  private plResidualReloadSub?: any;

  constructor(cdref: ChangeDetectorRef,
              messageService: MessageService,
              translateService: TranslateService,
              private plResidualService: ClearingAllocationPlResidualService,
              private appConfigService: AppConfigService) {
    super(cdref, messageService,
      translateService);
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  protected initConfigs() {
    super.initConfigs();

    this.tableConfig = [
      {
        field: 'side',
        style: record => record.side == 'BUY' ? 'color: var(--yellowgreen) !important' : 'color: var(--tomato) !important;'
      },
      {
        field: 'volume',
      },
      {
        field: 'contractCode',
      },
      {
        field: 'maturity',
      },
      {
        field: 'callPut',
      },
      {
        field: 'strike',
      },
      {
        field: 'avgPriceGrpId',
        style: record => 'color: var(--yellow) !important'
      },
      {
        field: 'avgPrice',
        type: 'NUMERIC',
        format: '1.0-5',
        style: record => 'color: var(--yellow) !important'
      },
      {
        field: 'avgRndPrice',
        type: 'NUMERIC',
        format: '1.0-2',
        style: record => 'color: var(--yellow) !important'
      },
      {
        field: 'avgPriceResidual',
        type: 'NUMERIC',
        format: '1.0-2',
        style: record => 'color: var(--yellow) !important',
        // filterConfig: {type: 'numeric'}
      },
      {
        field: 'overrideAvgPriceResidual',
        type: 'NUMERIC',
        format: '1.0-2',
        style: record => 'color: var(--yellow) !important'
      },
      {
        field: 'overrideAvgPrice',
        type: 'NUMERIC',
        format: '1.0-2',
        style: record => 'color: var(--yellow) !important'
      },
      {
        field: 'plResidual',
        type: 'NUMERIC',
        format: '1.0',
        style: record => record.plResidual > 0 ? 'color: var(--yellowgreen) !important' : 'color: var(--tomato) !important;'
      },
      {
        field: 'customerName',
        //style: record => record.plResidual > 0 ? 'color: yellowgreen' : 'color: tomato;'
      },

    ];
    this.formConfig = [];
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();
    setTimeout(() => {
      this.startReloadRecordsLoop();
    });
  }

  startReloadRecordsLoop(): void {
    const intervalMs = this.appConfigService.clearingPalResidualTaskReloadInterval;

    // optional: ensure a single loop
    this.plResidualReloadSub?.unsubscribe();

    this.plResidualReloadSub = timer(0, intervalMs)
      .pipe(
        takeUntil(this.destroy$),

        // wait until request exists
        filter(() => !!this.tableCurrentRequest),

        // prepare request before calling API
        tap(() => this.preparePlResidualRequest()),

        // one request at a time
        exhaustMap(() => {
          const req = this.tableCurrentRequest!;
          const call$ = this.tradeModeEnabled
            ? this.plResidualService.listClearingIds(req)
            : this.plResidualService.listOrderIds(req);

          return call$.pipe(
            catchError(err => {
              this.errorMessage(String(err));
              return EMPTY;
            }),
          );
        }),
      )
      .subscribe(result => this.treatRecordsResult(result));
  }

  private treatRecordsResult(tableRecordResult: TableRecordResult<PlResidualInfo>): void {
    if (!tableRecordResult.success) {
      this.errorMessage(tableRecordResult.message);
      return;
    }

    if (!tableRecordResult.syncMetadata?.needSync) {
      // nothing to update this tick
      return;
    }

    if (this.totalRecords !== tableRecordResult.total) {
      this.records.splice(0, this.records.length);
    }

    this.totalRecords = tableRecordResult.total;
    this.inProgressCount = 0;

    this.applyRecordResult(
      tableRecordResult
    );

    this.records = [...this.records];

    setTimeout(() => {
      if (this.ccTable) this.ccTable.reloadTableValueMap();
    });

    this.recordsChange.emit(this.records);

    this.apiMessage(tableRecordResult);

    this.tableLoading = false;
    this.tableCurrentRequest!.syncMetadata = tableRecordResult.syncMetadata;
    this.markForCheck();
  }

  private preparePlResidualRequest(): void {
    const req = this.tableCurrentRequest!;
    const selected = JSON.stringify(this.selectedOrderIds);

    const changed =
      this.previousTradeModeEnabled !== undefined &&
      (
        this.tradeModeEnabled !== this.previousTradeModeEnabled ||
        (this.tradeModeEnabled && req.clearingIds && selected !== JSON.stringify(req.clearingIds)) ||
        (!this.tradeModeEnabled && req.orderIds && selected !== JSON.stringify(req.orderIds))
      );

    if (changed) {
      this.tableLoading = true;
      req.syncMetadata = null;
    }

    if (this.tradeModeEnabled) {
      req.clearingIds = this.selectedOrderIds;
      req.orderIds = null;
    } else {
      req.orderIds = this.selectedOrderIds;
      req.clearingIds = null;
    }

    this.previousTradeModeEnabled = this.tradeModeEnabled;
  }


// private getRecordResults(tableRecordResult) {
  //   if (tableRecordResult.success) {
  //   } else {
  //     this.errorMessage(tableRecordResult.message);
  //     this.tableLoading = false;
  //   }
  //   //refresh
  //   this.tableCurrentRequest.syncMetadata = tableRecordResult.syncMetadata;
  //   // setTimeout(() => {
  //   //   this.reLoadRecords()
  //   // }, this.loadRecordsInterval);
  // }

  private applyRecordResult(tableRecordResult) {
    this.records = tableRecordResult.records;
    this.globalResidual = this.recalculateGlobalPnL();
  }

  private recalculateGlobalPnL(): number {
    let globalPnL = 0;
    this.records.forEach(function (record: PlResidualInfo) {
      globalPnL += record.plResidual;
    });
    return globalPnL;

  }

  onRowDblClick($event: any) {

  }

  showDialogForm(record: PlResidualInfo): void {
    this.detailDialogVisible = true;
  }

  closeDialogForm(): void {
    this.detailDialogVisible = false;
  }


  needLoadRecords(request: PLRequest) {
    // console.log("----> tableRecordResult " + JSON.stringify(tableRecordResult));
    if (request) {
      // if (this.tableCurrentRequest && ((this.tableCurrentRequest.filters.length === 0 && request.filters.length > 0)
      //   || (JSON.stringify(this.tableCurrentRequest.filters) != JSON.stringify(request.filters))
      //   || (this.tableCurrentRequest.sortField !== request.sortField)
      //   || (this.tableCurrentRequest.sortOrder != request.sortOrder)
      //   || (this.tableCurrentRequest.globalFilter !== request.globalFilter))) {
      //   this.tableLoading = true;
      //   this.totalRecords = 0;
      //   this.forceReload = true;
      //   console.log("----> forceReload " + this.forceReload);
      // }
      this.tableLoading = true;
      request.syncMetadata = null;
      this.tableCurrentRequest = request;
      // if (this.tableCurrentRequest && !this.tableCurrentRequest.sortOrder) {
      //   this.tableCurrentRequest.sortOrder = 1;
      //   this.tableCurrentRequest.sortField = 'id';
      // }
    }
  }
}
